import React, { useEffect, useMemo, useState } from 'react'
import useDebounce from 'react-use/lib/useDebounce'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import MainInterface from './mainInterface.component'
import useAPI from '~/prix/react/hooks/api'
import PulseLoaderComponent from '~/prix/react/components/pulseLoader'
import { SelectedInsight } from './insightsModal.component'
import { useLocation, useNavigate } from 'react-router-dom'
import Screen from '~/components/screen'

interface SentinelLocationState {
  baseLayers?: Layer[]
  selectedInsight?: SelectedInsight | null
}

export default function MapScreen() {
  const { isLoading: isLoadingAPI, context } = useAPI()
  const location = useLocation()
  const navigate = useNavigate()

  const locationState = location.state as SentinelLocationState | null

  const [selectedInsight, setSelectedInsight] = useState<SelectedInsight | null>(null)
  const [readySelectedInsight, setDebouncedSelectedInsight] = useState<SelectedInsight | null>(null)
  const [baseLayers, setBaseLayers] = useState<Layer[]>([])

  const isRestoringFromHistory = React.useRef(false)

  useDebounce(
    () => {
      setDebouncedSelectedInsight(selectedInsight)
    },
    1200,
    [selectedInsight],
  )

  useEffect(() => {
    if (!context.user) return

    if (isRestoringFromHistory.current) {
      isRestoringFromHistory.current = false
      return
    }

    if (baseLayers.length === 0 && !selectedInsight) {
      return
    }

    const isDifferentFromLocation =
      JSON.stringify(locationState?.baseLayers) !== JSON.stringify(baseLayers) ||
      JSON.stringify(locationState?.selectedInsight) !== JSON.stringify(selectedInsight)

    if (!isDifferentFromLocation) {
      return
    }

    navigate('.', {
      replace: false,
      state: {
        baseLayers,
        selectedInsight,
      } as SentinelLocationState,
    })
  }, [context.user, baseLayers, selectedInsight, locationState?.baseLayers, locationState?.selectedInsight, navigate])

  useEffect(() => {
    if (locationState) {
      const shouldUpdateInsight =
        JSON.stringify(locationState.selectedInsight) !== JSON.stringify(selectedInsight)
      const shouldUpdateLayers =
        JSON.stringify(locationState.baseLayers) !== JSON.stringify(baseLayers)

      if (shouldUpdateInsight || shouldUpdateLayers) {
        isRestoringFromHistory.current = true

        if (shouldUpdateInsight) {
          setSelectedInsight(locationState.selectedInsight ?? null)
          setDebouncedSelectedInsight(locationState.selectedInsight ?? null)
        }
        if (shouldUpdateLayers) {
          setBaseLayers(locationState.baseLayers ?? [])
        }
      }
    }
  }, [locationState?.selectedInsight, locationState?.baseLayers])

  const layers: Layer[] = useMemo(() => {
    if (!readySelectedInsight || !readySelectedInsight.preview) {
      return baseLayers
    }

    const newLayers = [...baseLayers]
    newLayers[readySelectedInsight.baseLayerIndex] = {
      variable:
        readySelectedInsight.preview.replacingVariable ||
        newLayers[readySelectedInsight.baseLayerIndex].variable,
      space:
        readySelectedInsight.preview.space || newLayers[readySelectedInsight.baseLayerIndex].space,
      time:
        readySelectedInsight.preview.time || newLayers[readySelectedInsight.baseLayerIndex].time,
    }
    return newLayers
  }, [baseLayers, readySelectedInsight?.preview])

  if (isLoadingAPI) {
    return <PulseLoaderComponent />
  }

  return (
    <Screen mustBeAuthenticated={!context.bypassAuth}>
      <MainInterface
        baseLayers={baseLayers}
        layers={layers}
        selectedInsight={selectedInsight}
        readySelectedInsight={readySelectedInsight}
        onChangeBaseLayers={setBaseLayers}
        onChangeSelectedInsight={setSelectedInsight}
      />
    </Screen>
  )
}
